/***********************
 * Alarm monitoring
 ************************/

import React, { useContext, useEffect, useRef, useState } from "react"
import { withTranslation } from "react-i18next"
import PropTypes from "prop-types"
import GlobalState from "contexts/GlobalState"
import Breadcrumbs from "components/Common/Breadcrumb"
import QuickGuide from "components/QuickGuides/QuickGuide"
import alarmAudio from "assets/audio/alarm_received.mp3"
import MonitoringAlarmTable from "./AlarmMonitoringTable"
import MonitoringHandoverTable from "./HandoverMonitoringTable"
import AlarmMonitoringDrawer from "./AlarmMonitoringDrawer"
import {
  getMonitoringAlarms,
  getMonitoringHandoverAlarms,
} from "helpers/backend_helper"
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Label,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap"
import { Select, Switch, Tooltip } from "antd"
import classnames from "classnames"
import { showToast } from "utils/general"
import { useNavigate } from "react-router-dom"

const AlarmMonitoring = props => {
  const [state] = useContext(GlobalState)
  const [quickGuide, setQuickGuide] = useState(false)
  const [alarms, setAlarms] = useState([])
  const [handovers, setHandovers] = useState([])
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState("1")
  const [selectedRow, setSelectedRow] = useState(null)
  const [showDrawer, setShowDrawer] = useState(false)
  const [latestAlarmDate, setLatestAlarmDate] = useState(null)
  const [count, setCount] = useState(0)
  const [updateRequired, setUpdateRequired] = useState(false)
  const [soundEffect, setSoundEffect] = useState(true)
  const [showNotification, setShowNotification] = useState(true)
  const [showResolvedAlarms, setShowResolvedAlarms] = useState(false)
  const [showResolvedHandovers, setShowResolvedHandovers] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [alarmTypes, setAlarmTypes] = useState(0)

  const navigate = useNavigate()
  const abortRef = useRef(null)
  useEffect(() => {
    document.title = props.t("Alarm monitoring") + " | Suvanto Care"
    abortRef.current = new AbortController()
    // Redirect if not permitted to access this page
    if (state.authUser != null) {
      const obj = JSON.parse(state.authUser)
      let role = obj.activeRole ? obj.activeRole : obj.role
      if (role != "system_admin" && !obj.access_permissions?.alarm_monitoring) {
        navigate("/main", { replace: true })
      }
    }

    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    //console.log("", count)
    if (autoRefresh && !showDrawer) {
      //console.log("STARTING")
      const interval = setInterval(() => {
        //console.log("UPDATE")
        clearInterval(interval)
        getAlarms()
      }, 60000)

      return () => {
        clearInterval(interval) /*console.log("CLEAR")*/
      }
    } else {
      //console.log("SKIP")
    }
  }, [count, autoRefresh])

  useEffect(() => {
    getAlarms()
  }, [showResolvedAlarms, alarmTypes])

  useEffect(() => {
    getHandoverAlarms()
  }, [showResolvedHandovers])

  useEffect(() => {
    if (!showDrawer && updateRequired) {
      setUpdateRequired(false)
      getAlarms()
    }
  }, [showDrawer])

  // Fetch alarms from server
  const getAlarms = () => {
    if (!loading) {
      setLoading(true)
      getMonitoringAlarms(abortRef.current, showResolvedAlarms, alarmTypes)
        .then(result => {
          //console.log("Alarms: ", result)
          if (result.statusCode == 200 && result.entries) {
            for (const element of result.entries) {
              element.description = "AlarmTitles." + element.alarm_id

              if (element.extra_info) {
                if (element.alarm_id == 30) { // Abnormal measurement
                  let split = element.extra_info.split(" ")
                  if (split.length == 2) {
                    element.extra_info = element.extra_info.replace(split[0], props.t(split[0]))
                  }
                }
                else if (element.alarm_id == 31) { // Measurement not done
                  element.extra_info = props.t(element.extra_info)
                }
              }
            }

            result.entries.sort((a, b) =>
              a.received_at < b.received_at ? 1 : -1
            )

            if (result.entries.length > 0) {
              if (result.entries[0].status == "UNASSIGNED") {
                if (
                  latestAlarmDate != null &&
                  result.entries[0].received_at > latestAlarmDate
                ) {
                  if (soundEffect) playSound()
                  if (showNotification)
                    showToast(props.t("New alarm received"), "info", 20000)
                }
                setLatestAlarmDate(result.entries[0].received_at)
              }
            }

            setAlarms(result.entries)
          } else {
            setAlarms([])
          }
        })
        .catch(err => {
          console.log(err)
        })
        .finally(() => {
          if (!abortRef.current.signal.aborted) {
            setLoading(false)
            setCount(value => value + 1)
          }
        })
    } else {
      setCount(value => value + 1)
    }
  }

  // Fetch handover alarms
  const getHandoverAlarms = () => {
    getMonitoringHandoverAlarms(abortRef.current, showResolvedHandovers)
      .then(result => {
        //console.log("Handovers: ", result)
        if (result.statusCode == 200 && result.entries) {
          for (const element of result.entries) {
            element.description = "AlarmTitles." + element.handover_alarm_id
          }
          result.entries.sort((a, b) =>
            a.received_at < b.received_at ? 1 : -1
          )
          setHandovers(result.entries)
        } else {
          setHandovers([])
        }
      })
      .catch(err => {
        console.log(err)
      })
      .finally(() => { })
  }

  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  // Play alarm sound
  const playSound = () => {
    let audioElement = document.getElementById("alarmAudio")
    if (audioElement != null) {
      audioElement.play()
    }
  }

  return (
    <>
      <div className="page-content">
        <Container fluid className={quickGuide ? "quick-guide-open" : ""}>
          <audio id="alarmAudio" className="d-none">
            <source src={alarmAudio} type="audio/mpeg" />
            <track kind="captions" />
          </audio>
          <Breadcrumbs
            title={props.t("Alarm monitoring")}
            className="breadcrumb m-0 justify-content-center"
            breadcrumbItem={
              <div className="d-flex align-items-center">
                <QuickGuide
                  source="alarmMonitoring"
                  title="Alarm monitoring"
                  isOpen={quickGuide}
                  setIsOpen={setQuickGuide}
                />
              </div>
            }
          />
          <Row className="align-items-center">
            <Col>
              <Card>
                <CardHeader className="bg-transparent border-bottom">
                  <div className="d-flex flex-wrap">
                    <ul className="nav nav-tabs nav-tabs-custom card-header-tabs">
                      <NavItem>
                        <NavLink
                          to="#"
                          className={classnames({ active: activeTab === "1" })}
                          onClick={() => {
                            toggleTab("1")
                          }}
                        >
                          {props.t("Alarms")}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === "2" })}
                          onClick={() => {
                            toggleTab("2")
                          }}
                        >
                          {props.t("Handover alarms")}
                        </NavLink>
                      </NavItem>
                    </ul>
                  </div>
                </CardHeader>
                <CardBody>
                  <TabContent activeTab={activeTab} className="py-2">
                    <Row className="align-items-center mb-3">
                      <Col className="col-auto">
                        <Label className="my-0">
                          {activeTab == 1 ? (
                            <Switch
                              className="me-2"
                              name="show_resolved"
                              checkedChildren={
                                <i className="mdi mdi-check-bold" />
                              }
                              unCheckedChildren={
                                <i className="mdi mdi-close-thick" />
                              }
                              checked={showResolvedAlarms}
                              onChange={v => setShowResolvedAlarms(v)}
                            />
                          ) : (
                            activeTab == 2 && (
                              <Switch
                                className="me-2"
                                name="show_resolved_2"
                                checkedChildren={
                                  <i className="mdi mdi-check-bold" />
                                }
                                unCheckedChildren={
                                  <i className="mdi mdi-close-thick" />
                                }
                                checked={showResolvedHandovers}
                                onChange={v => setShowResolvedHandovers(v)}
                              />
                            )
                          )}
                          {props.t("Show resolved alarms")}
                        </Label>
                      </Col>

                      {activeTab == 1 && (
                        <>
                          <Col className="col-auto">
                            <Label className="my-0">
                              <Switch
                                style={{
                                  backgroundColor: soundEffect ? "#34c38f" : "",
                                }}
                                className="me-2"
                                name="play_sound"
                                checkedChildren={
                                  <i className="mdi mdi-bell-ring" />
                                }
                                unCheckedChildren={
                                  <i className="mdi mdi-bell-off" />
                                }
                                checked={soundEffect}
                                onChange={v => setSoundEffect(v)}
                              />
                              {props.t("Sound")}
                            </Label>
                          </Col>
                          <Col className="col-auto">
                            <Label className="my-0">
                              <Switch
                                style={{
                                  backgroundColor: showNotification
                                    ? "#34c38f"
                                    : "",
                                }}
                                className="me-2"
                                name="show_notification"
                                checkedChildren={
                                  <i className="mdi mdi-message" />
                                }
                                unCheckedChildren={
                                  <i className="mdi mdi-message-off" />
                                }
                                checked={showNotification}
                                onChange={v => setShowNotification(v)}
                              />
                              {props.t("Notification")}
                            </Label>
                          </Col>
                        </>
                      )}
                      <Col className="col-auto ms-auto form-inline">
                        <Select
                          style={{ width: "180px" }}
                          className="me-3"
                          disabled={loading}
                          value={alarmTypes}
                          onChange={v => {
                            setAlarmTypes(v)
                          }}
                          options={[
                            { value: 0, label: props.t("Show all") },
                            { value: 1, label: props.t("Customer alarms") },
                            { value: 2, label: props.t("Device alarms") },
                          ]}
                        />
                        <Tooltip
                          title={props.t("Automatic refresh") + " (60s)"}
                        >
                          <Switch
                            checked={autoRefresh}
                            onChange={setAutoRefresh}
                            checkedChildren={<i className="mdi mdi-sync" />}
                            unCheckedChildren={
                              <i className="mdi mdi-sync-off" />
                            }
                          />
                        </Tooltip>

                        <Button
                          className="ms-3"
                          color="primary"
                          style={{
                            borderRadius: "100px",
                            paddingTop: "4px",
                            paddingBottom: "4px",
                          }}
                          disabled={loading}
                          onClick={getAlarms}
                        >
                          <i className="mdi mdi-refresh me-2" />
                          {props.t("Refresh")}
                        </Button>
                      </Col>
                    </Row>

                    <TabPane className="show" tabId="1">
                      <MonitoringAlarmTable
                        data={alarms}
                        setShowDrawer={setShowDrawer}
                        setSelectedRow={setSelectedRow}
                        loading={loading}
                      />
                    </TabPane>
                    <TabPane className="show" tabId="2">
                      <MonitoringHandoverTable alarms={handovers} />
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <AlarmMonitoringDrawer
            showDrawer={showDrawer}
            setShowDrawer={setShowDrawer}
            selectedRow={selectedRow}
            setUpdateRequired={setUpdateRequired}
          />
        </Container>
      </div>
    </>
  )
}

AlarmMonitoring.propTypes = {
  t: PropTypes.any,
}
export default withTranslation()(AlarmMonitoring)
